import { EventItem, NewEvent } from '../types';
import { getStoredLegacyToken } from './legacyAuthService';

const isDev = Boolean((import.meta as any).env?.DEV);
const logDebug = (...args: any[]) => {
  if (isDev) console.log(...args);
};

type ApiEvent = {
  id: string;
  experienceId?: string | number | null;
  experienceName?: string | null;
  title?: string;
  description?: string;
  location?: string;
  imageUrl?: string;
  startAt?: string | null;
  endAt?: string | null;
  allDay?: boolean;
  category?: string;
  tags?: string[];
  price?: string;
  organizer?: string;
  contactInfo?: string;
  website?: string;
  coordinates?: { latitude?: number; longitude?: number } | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type EventsListResponse = {
  success: boolean;
  events?: ApiEvent[];
  error?: string;
};

type GenericResponse = {
  success: boolean;
  eventId?: string;
  error?: string;
};

function parseDate(value: unknown, fallback?: Date): Date {
  const raw = String(value || '').trim();
  if (!raw) return fallback || new Date();
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return fallback || new Date();
  return parsed;
}

function toEventItem(event: ApiEvent): EventItem {
  const now = new Date();
  const startAt = parseDate(event.startAt, now);
  const endAtRaw = String(event.endAt || '').trim();
  const endAt = endAtRaw ? parseDate(endAtRaw) : undefined;

  return {
    id: String(event.id || ''),
    experienceId: event.experienceId != null ? String(event.experienceId) : '',
    experienceName: String(event.experienceName || ''),
    title: String(event.title || ''),
    description: String(event.description || ''),
    location: String(event.location || ''),
    imageUrl: String(event.imageUrl || ''),
    startAt,
    endAt,
    allDay: Boolean(event.allDay),
    category: String(event.category || ''),
    tags: Array.isArray(event.tags) ? event.tags.filter(Boolean) : [],
    price: String(event.price || ''),
    organizer: String(event.organizer || ''),
    contactInfo: String(event.contactInfo || ''),
    website: String(event.website || ''),
    coordinates:
      event.coordinates
      && Number.isFinite(Number(event.coordinates.latitude))
      && Number.isFinite(Number(event.coordinates.longitude))
        ? {
          latitude: Number(event.coordinates.latitude),
          longitude: Number(event.coordinates.longitude),
        }
        : undefined,
    createdAt: parseDate(event.createdAt, now),
    updatedAt: parseDate(event.updatedAt, now),
  };
}

async function requestJson<T>(path: string, init: RequestInit): Promise<T> {
  const token = getStoredLegacyToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers.Authorization = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
  }

  const response = await fetch(path, {
    ...init,
    headers,
  });

  const raw = await response.text();
  let parsed: any = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    const detail = parsed?.error || parsed?.message || raw || `Errore ${response.status}`;
    throw new Error(detail);
  }

  return (parsed || {}) as T;
}

function isUpcomingOrOngoing(event: EventItem, now: Date): boolean {
  if (event.startAt >= now) return true;
  if (event.endAt && event.endAt > now) return true;
  if (event.allDay) {
    const today = new Date();
    const eventDate = new Date(event.startAt);
    return (
      eventDate.getFullYear() === today.getFullYear()
      && eventDate.getMonth() === today.getMonth()
      && eventDate.getDate() === today.getDate()
    );
  }
  return false;
}

export async function fetchAllEvents(): Promise<EventItem[]> {
  try {
    logDebug('🔄 [fetchAllEvents] Richiesta API /api/events/list');
    const payload = await requestJson<EventsListResponse>('/api/events/list?limit=2000', { method: 'GET' });
    const events = Array.isArray(payload.events) ? payload.events.map(toEventItem) : [];
    events.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    logDebug('✅ [fetchAllEvents] Eventi caricati:', events.length);
    return events;
  } catch (error) {
    console.error('❌ [fetchAllEvents] Errore nel recupero di tutti gli eventi:', error);
    return [];
  }
}

export async function fetchUpcomingEvents(): Promise<EventItem[]> {
  try {
    const now = new Date();
    const all = await fetchAllEvents();
    return all.filter((event) => isUpcomingOrOngoing(event, now));
  } catch (error) {
    console.error('Errore nel recupero degli eventi:', error);
    return [];
  }
}

export async function createEvent(eventData: NewEvent): Promise<string> {
  const normalizedPayload = {
    ...eventData,
    experienceId: String(eventData.experienceId || '').trim() || null,
    experienceName: String(eventData.experienceName || '').trim() || null,
  };
  const payload = await requestJson<GenericResponse>('/api/events/create', {
    method: 'POST',
    body: JSON.stringify(normalizedPayload),
  });
  if (!payload.success || !payload.eventId) {
    throw new Error(payload.error || 'Creazione evento non riuscita');
  }
  return payload.eventId;
}

export async function updateEvent(eventId: string, eventData: Partial<NewEvent>): Promise<void> {
  const normalizedPayload = {
    ...eventData,
    experienceId:
      eventData.experienceId === undefined
        ? undefined
        : String(eventData.experienceId || '').trim() || null,
    experienceName:
      eventData.experienceName === undefined
        ? undefined
        : String(eventData.experienceName || '').trim() || null,
  };
  const payload = await requestJson<GenericResponse>('/api/events/update', {
    method: 'POST',
    body: JSON.stringify({
      eventId,
      ...normalizedPayload,
    }),
  });
  if (!payload.success) {
    throw new Error(payload.error || 'Aggiornamento evento non riuscito');
  }
}

export async function deleteEvent(eventId: string): Promise<void> {
  const payload = await requestJson<GenericResponse>('/api/events/delete', {
    method: 'POST',
    body: JSON.stringify({ eventId }),
  });
  if (!payload.success) {
    throw new Error(payload.error || 'Eliminazione evento non riuscita');
  }
}
